import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Injector, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { Validators } from '@angular/forms';
import { SelectDropdownPositionDirective } from '../../directives/select-dropdown-position.directive';

@Component({
  selector: 'app-select-input',
  imports: [FormsModule, NgIf, NgFor, NgClass, NgSelectModule, NgSelectComponent, SelectDropdownPositionDirective],
  templateUrl: './select-input.component.html',
  styleUrl: './select-input.component.css',
  host: { '[class.checkbox-select]': 'show_checkboxes' },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SelectInputComponent
    }
  ],
  standalone: true,
})
export class SelectInputComponent implements ControlValueAccessor, OnChanges {
  @Input() options: any[] = [];
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() is_disabled: boolean = false;
  // Set false to hide the ng-select clear ("x") button even when a value is selected — the field
  // stays selectable/openable, just not clearable (e.g. an evaluator already mid-evaluation).
  @Input() clearable: boolean = true;
  @Input() is_required: boolean = false;
  @Input() is_multiple: boolean = false;
  @Input() show_checkboxes: boolean = false;
  @Input() patch_first_value: boolean = false;
  control: any;
  value = '';
  display_value = '';
  display_object: any = {};
  required_validator = Validators.required;

  selectedValues: string | any = '';

  @Output() valueChange = new EventEmitter<string | any>();
  onChange = (selected: any) => { };
  onTouched = () => { };

  @Input() depended: any = 0;
  @Input() appendTo: any = 'body';
  @Input() show_email: boolean = false;

  @Output() registerSelf = new EventEmitter<{ depended: any, ref: any }>();

  constructor(private injector: Injector) { }

  ngOnInit(): void {
    this.registerSelf.emit({ depended: this.depended, ref: this });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      if (this.patch_first_value && this.options?.length > 0 && !this.selectedValues) {
        setTimeout(() => {
          this.onSelectionChange(this.options[0]);
        });
      } else if (this.value !== undefined && this.value !== null && this.value !== '') {
        // Re-evaluate selectedValues when options load/change and a value is already set
        this.writeValue(this.value);
      }
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const ngControl: NgControl | null = this.injector.get(NgControl, null);
      if (ngControl) {
        this.control = ngControl.control as FormControl;
        this.value = this.control?.value
      } else {
        // Component is missing form control binding
      }
    }, 100);
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
    if (this.is_disabled) {
      this.disabled = true;
    }
  }

  writeValue(value: any): void {
    if (this.is_multiple) {
      if (Array.isArray(value)) {
        this.selectedValues = value
          .map(v => this.options?.find(option => option.value == v))
          .filter(option => option !== undefined) || [];
      } else if (typeof value === 'string') {
        // Handle comma separated string if applicable
        this.selectedValues = value.split(',').map(v => v.trim())
          .map(v => this.options?.find(option => option.value == v))
          .filter(option => option !== undefined) || [];
      } else {
        this.selectedValues = [];
      }
    } else {
      if (value !== undefined && value !== null) {
        if (typeof value === 'object' && 'value' in value) {
          // value is already an option object — find matching in options or use as-is
          this.selectedValues = this.options?.find(opt => opt.value == value.value) || value;
        } else {
          this.selectedValues = this.options?.find(option => option.value == value) || null;
        }
      } else {
        this.selectedValues = null;
      }
    }
    this.value = (value && typeof value === 'object' && 'value' in value) ? value.value : value;

    setTimeout(() => {
      // For single select, update display if needed
      if (!this.is_multiple && value !== undefined && value !== null) {
        this.change_display_value(value);
      }
    });
  }

  change_display_value(value: string) {
    this.display_value = this.options?.find((item: any) => item.value == value)?.title || '';
    this.display_object = this.options?.find((item: any) => item.value == value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onSelectionChange(selected: any) {
    let formValue;
    if (this.is_multiple) {
      this.selectedValues = selected || [];
      formValue = this.selectedValues.map((item: any) => item.value);
    } else {
      this.selectedValues = selected;
      formValue = selected ? selected.value : null;
      this.change_display_value(formValue);
    }

    this.onChange(formValue);
    this.value = formValue;
    // Emit the selected object(s) so parent can access details if needed
    this.valueChange.emit({ value: this.selectedValues, depended: this.depended });
    this.onTouched();
  }

  removeItem(item: any) {
    if (this.is_multiple && Array.isArray(this.selectedValues)) {
      const newSelection = this.selectedValues.filter((v: any) => v.value !== item.value);
      this.onSelectionChange(newSelection);
    }
  }

  // Clearing a dependent dropdown must also drop `value`/display state, not just `selectedValues`.
  // ngOnChanges re-applies `value` whenever the options list is swapped, so a stale `value` that
  // still exists in the new option list (shared masters like an academic year reused by several
  // universities) would silently re-select itself after the parent dropdown changed.
  reset() {
    this.selectedValues = this.is_multiple ? [] : null;
    this.value = '';
    this.display_value = '';
    this.display_object = {};
    this.onChange(this.selectedValues);
    this.valueChange.emit({ value: this.selectedValues, depended: this.depended });
  }

  compareFn(option1: any, option2: any): boolean {
    return option1 && option2 ? option1.value === option2.value : option1 === option2;
  }

  capitalizeFirstLetter(value: string): string {
    if (!value) return value;
    value = value + ''
    return (value).charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }
}